import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './Jobs.scss';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Loader from '../../components/Loader';
import { format } from 'date-fns';
import JobApplicationForm from './JobApplicationForm';

const FILTER_VALUES_URL =
  process.env.REACT_APP_JOBS_FILTER_VALUES_URL || 'https://jobs.thepioneers.ai/website/filter-values';
const JOBS_API_URL = process.env.REACT_APP_JOBS_API_URL || 'https://jobs.thepioneers.ai';
const NESTED_FILTER_SEPARATOR = ':::';

const createEmptyFilters = () => ({
  locations: [],
  disciplinesSpecialties: [],
  shiftPatterns: [],
  shiftTypes: [],
});

const createNestedFilterValue = (parent, child = '') => `${parent}${NESTED_FILTER_SEPARATOR}${child}`;

const groupNestedFilterValues = (values, availableGroups = {}) => {
  const groups = values.reduce((groupedValues, value) => {
    const [parent, child = ''] = value.split(NESTED_FILTER_SEPARATOR);
    if (!parent) return groupedValues;

    if (!groupedValues[parent]) groupedValues[parent] = [];
    if (child && !groupedValues[parent].includes(child)) groupedValues[parent].push(child);

    return groupedValues;
  }, {});

  Object.entries(groups).forEach(([parent, selectedChildren]) => {
    const availableChildren = availableGroups[parent] || [];
    if (availableChildren.length && availableChildren.every(child => selectedChildren.includes(child))) {
      groups[parent] = [];
    }
  });

  return groups;
};

const createApiFilters = (selectedFilters, filterValues = {}) => {
  const filters = {};
  const locations = groupNestedFilterValues(selectedFilters.locations, filterValues.locations);
  const disciplinesSpecialties = groupNestedFilterValues(
    selectedFilters.disciplinesSpecialties,
    filterValues.disciplinesSpecialties,
  );

  if (Object.keys(locations).length) filters.location = locations;
  if (Object.keys(disciplinesSpecialties).length) filters.disciplines_specialties = disciplinesSpecialties;
  if (selectedFilters.shiftPatterns.length) filters.shift_patterns = selectedFilters.shiftPatterns;
  if (selectedFilters.shiftTypes.length) filters.shift_types = selectedFilters.shiftTypes;

  return filters;
};

const createJobsUrl = (key, searchValue, selectedFilters, filterValues) => {
  const params = new URLSearchParams();
  const filters = createApiFilters(selectedFilters, filterValues);
  const query = searchValue.trim().slice(0, 100);

  if (Object.keys(filters).length) params.set('filters', JSON.stringify(filters));
  if (query) params.set('query', query);
  if (key) params.set('last_key', key);

  const queryString = params.toString();
  const baseUrl = `${JOBS_API_URL}/job/organization/healthcareppl/description/`;
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

const FilterChevron = ({ isOpen }) => (
  <svg className={isOpen ? 'jobsFilterChevron jobsFilterChevronOpen' : 'jobsFilterChevron'} width='16' height='10' viewBox='0 0 16 10' fill='none' aria-hidden='true'>
    <path d='M1.5 1.5L8 8L14.5 1.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const Library = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValues, setFilterValues] = useState({
    disciplinesSpecialties: {},
    locations: {},
    shiftPatterns: [],
    shiftTypes: [],
  });
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(createEmptyFilters);
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(createEmptyFilters);
  const [locationSearch, setLocationSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    location: true,
    disciplinesSpecialties: false,
    shiftPatterns: false,
    shiftTypes: false,
  });
  const [expandedLocations, setExpandedLocations] = useState([]);
  const [expandedDisciplines, setExpandedDisciplines] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeys, setPageKeys] = useState([null]);
  const [nextPageKey, setNextPageKey] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const jobsListTopRef = useRef(null);

  const getJobs = useCallback(async (
    key = null,
    filters = { searchValue: '', selectedFilters: createEmptyFilters() },
    availableFilterValues = {},
  ) => {
    setIsLoading(true);

    try {
      const url = createJobsUrl(key, filters.searchValue, filters.selectedFilters, availableFilterValues);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch jobs: ${response.status}`);

      const res = await response.json();
      const jobs = res.Items || [];

      setFilteredRows(jobs);
      if (res.total !== undefined && res.total !== null && Number.isFinite(Number(res.total))) {
        setTotalJobs(Number(res.total));
      } else if (!key) {
        setTotalJobs(jobs.length);
      }
      setNextPageKey(res.last_key || null);

      return true;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getJobs();
  }, [getJobs]);

  useEffect(() => {
    const controller = new AbortController();

    const getFilterValues = async () => {
      setIsFiltersLoading(true);
      setFiltersError('');

      try {
        const response = await fetch(FILTER_VALUES_URL, { signal: controller.signal });
        if (!response.ok) throw new Error(`Failed to fetch job filters: ${response.status}`);

        const result = await response.json();
        const items = result.Items;
        if (!items || typeof items !== 'object') throw new Error('Job filters have an invalid response format');

        const locations = items.location || {};
        setFilterValues({
          disciplinesSpecialties: items.disciplines_specialties || {},
          locations,
          shiftPatterns: items.shift_pattern || [],
          shiftTypes: items.shift_type || [],
        });

        const firstLocation = Object.keys(locations)[0];
        if (firstLocation) setExpandedLocations([firstLocation]);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching job filters:', error);
        setFiltersError('Filters are temporarily unavailable. Please try again later.');
      } finally {
        if (!controller.signal.aborted) setIsFiltersLoading(false);
      }
    };

    getFilterValues();
    return () => controller.abort();
  }, []);

  const visibleLocations = useMemo(() => {
    const query = locationSearch.trim().toLowerCase();

    return Object.entries(filterValues.locations).reduce((results, [state, cities]) => {
      if (!query || state.toLowerCase().includes(query)) {
        results.push([state, cities]);
        return results;
      }

      const matchingCities = cities.filter(city => city.toLowerCase().includes(query));
      if (matchingCities.length) results.push([state, matchingCities]);
      return results;
    }, []);
  }, [filterValues.locations, locationSearch]);

  useEffect(() => {
    if (!selectedJob) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setSelectedJob(null);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedJob]);

  const handleSearch = useCallback(async () => {
    const didLoad = await getJobs(null, { searchValue, selectedFilters }, filterValues);
    if (!didLoad) return;

    setAppliedSearchValue(searchValue.trim().slice(0, 100));
    setAppliedFilters(selectedFilters);
    setCurrentPage(1);
    setPageKeys([null]);
    requestAnimationFrame(() => jobsListTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [filterValues, getJobs, searchValue, selectedFilters]);

  const toggleSection = section => {
    setExpandedSections(previousSections => ({
      ...previousSections,
      [section]: !previousSections[section],
    }));
  };

  const toggleExpandedGroup = (group, setGroups) => {
    setGroups(previousGroups =>
      previousGroups.includes(group)
        ? previousGroups.filter(item => item !== group)
        : [...previousGroups, group],
    );
  };

  const toggleFilterValue = (filterName, value) => {
    setSelectedFilters(previousFilters => ({
      ...previousFilters,
      [filterName]: previousFilters[filterName].includes(value)
        ? previousFilters[filterName].filter(item => item !== value)
        : [...previousFilters[filterName], value],
    }));
  };

  const toggleNestedFilterGroup = (filterName, parent, children) => {
    const groupValues = children.length
      ? children.map(child => createNestedFilterValue(parent, child))
      : [createNestedFilterValue(parent)];

    setSelectedFilters(previousFilters => {
      const isSelected = groupValues.every(value => previousFilters[filterName].includes(value));
      const nextValues = isSelected
        ? previousFilters[filterName].filter(value => !groupValues.includes(value))
        : [...new Set([...previousFilters[filterName], ...groupValues])];

      return { ...previousFilters, [filterName]: nextValues };
    });
  };

  const isNestedGroupSelected = (filterName, parent, children) => {
    const groupValues = children.length
      ? children.map(child => createNestedFilterValue(parent, child))
      : [createNestedFilterValue(parent)];

    return groupValues.every(value => selectedFilters[filterName].includes(value));
  };

  const resetFilter = filterName => {
    setSelectedFilters(previousFilters => ({ ...previousFilters, [filterName]: [] }));
  };

  const resetAllFilters = async () => {
    const emptyFilters = createEmptyFilters();
    setSearchValue('');
    setLocationSearch('');
    setSelectedFilters(emptyFilters);
    setAppliedSearchValue('');
    setAppliedFilters(emptyFilters);

    const didLoad = await getJobs(null, { searchValue: '', selectedFilters: emptyFilters }, filterValues);
    if (didLoad) {
      setCurrentPage(1);
      setPageKeys([null]);
    }
  };

  const handleNextPage = async () => {
    if (!nextPageKey || isLoading) return;

    const didLoad = await getJobs(nextPageKey, {
      searchValue: appliedSearchValue,
      selectedFilters: appliedFilters,
    }, filterValues);

    if (didLoad) {
      setPageKeys(previousKeys => {
        const newKeys = [...previousKeys];
        newKeys[currentPage] = nextPageKey;
        return newKeys;
      });
      setCurrentPage(previousPage => previousPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage === 1 || isLoading) return;

    const previousPageKey = pageKeys[currentPage - 2];
    const didLoad = await getJobs(previousPageKey, {
      searchValue: appliedSearchValue,
      selectedFilters: appliedFilters,
    }, filterValues);

    if (didLoad) {
      setCurrentPage(previousPage => previousPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className='page'>
      <Header />
      <div className='libraryPage jobsLibraryPage'>
        <span className='libraryTitle'>FIND YOUR DREAM JOB</span>
        <span className='libraryText'>Discover exciting healthcare opportunities that match your skills and career goals</span>
        {!isLoading ? (
          <div className='libraryContent'>
            <div className='libraryLeft'>
              <div className='librarySearch'>
                <div className='jobsFiltersTitleRow'>
                  <span className='librarySearchText'>FILTER JOBS</span>
                  <button type='button' className='jobsFiltersResetAll' onClick={resetAllFilters}>Reset All</button>
                </div>
                <input
                  className='librarySearchBox'
                  placeholder='Search jobs'
                  aria-label='Search jobs'
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') handleSearch();
                  }}
                  maxLength={100}
                />
                {isFiltersLoading ? (
                  <span className='jobsFiltersStatus'>Loading filters…</span>
                ) : filtersError ? (
                  <span className='jobsFiltersStatus jobsFiltersError'>{filtersError}</span>
                ) : (
                  <div className='jobsFilters'>
                    <section className='jobsFilterSection'>
                      <div className='jobsFilterHeader'>
                        <button type='button' className='jobsFilterToggle' onClick={() => toggleSection('location')} aria-expanded={expandedSections.location}>
                          <span>Location</span>
                        </button>
                        {!!selectedFilters.locations.length && <button type='button' className='jobsFilterReset' onClick={() => resetFilter('locations')}>Reset</button>}
                        <button type='button' className='jobsFilterSectionChevron' onClick={() => toggleSection('location')} aria-label={`${expandedSections.location ? 'Collapse' : 'Expand'} Location`} aria-expanded={expandedSections.location}>
                          <FilterChevron isOpen={expandedSections.location} />
                        </button>
                      </div>
                      {expandedSections.location && (
                        <div className='jobsFilterBody'>
                          <input
                            className='jobsFilterSearch'
                            placeholder='Search by state or city'
                            aria-label='Search by state or city'
                            value={locationSearch}
                            onChange={event => setLocationSearch(event.target.value)}
                          />
                          <div className='jobsFilterOptions jobsFilterNestedOptions'>
                            {visibleLocations.map(([state, cities]) => {
                              const isExpanded = !!locationSearch || expandedLocations.includes(state);
                              return (
                                <div className='jobsFilterGroup' key={state}>
                                  <div className='jobsFilterGroupRow'>
                                    <label className='jobsFilterOption jobsFilterGroupLabel'>
                                      <input
                                        type='checkbox'
                                        checked={isNestedGroupSelected('locations', state, filterValues.locations[state])}
                                        onChange={() => toggleNestedFilterGroup('locations', state, filterValues.locations[state])}
                                      />
                                      <span className='jobsFilterCheckboxMark' />
                                      <span>{state}</span>
                                    </label>
                                    <button type='button' className='jobsFilterGroupToggle' onClick={() => toggleExpandedGroup(state, setExpandedLocations)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${state}`} aria-expanded={isExpanded}>
                                      <FilterChevron isOpen={isExpanded} />
                                    </button>
                                  </div>
                                  {isExpanded && (
                                    <div className='jobsFilterChildren'>
                                      {cities.map(city => {
                                        const value = createNestedFilterValue(state, city);
                                        return (
                                          <label className='jobsFilterOption' key={value}>
                                            <input type='checkbox' checked={selectedFilters.locations.includes(value)} onChange={() => toggleFilterValue('locations', value)} />
                                            <span className='jobsFilterCheckboxMark' />
                                            <span>{city}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {!visibleLocations.length && <span className='jobsFiltersEmpty'>No locations found</span>}
                          </div>
                        </div>
                      )}
                    </section>

                    <section className='jobsFilterSection'>
                      <div className='jobsFilterHeader'>
                        <button type='button' className='jobsFilterToggle' onClick={() => toggleSection('disciplinesSpecialties')} aria-expanded={expandedSections.disciplinesSpecialties}>
                          <span>Discipline &amp; Specialty</span>
                        </button>
                        {!!selectedFilters.disciplinesSpecialties.length && <button type='button' className='jobsFilterReset' onClick={() => resetFilter('disciplinesSpecialties')}>Reset</button>}
                        <button type='button' className='jobsFilterSectionChevron' onClick={() => toggleSection('disciplinesSpecialties')} aria-label={`${expandedSections.disciplinesSpecialties ? 'Collapse' : 'Expand'} Discipline & Specialty`} aria-expanded={expandedSections.disciplinesSpecialties}>
                          <FilterChevron isOpen={expandedSections.disciplinesSpecialties} />
                        </button>
                      </div>
                      {expandedSections.disciplinesSpecialties && (
                        <div className='jobsFilterBody'>
                          <div className='jobsFilterOptions jobsFilterNestedOptions'>
                            {Object.entries(filterValues.disciplinesSpecialties).map(([discipline, specialties]) => {
                              const isExpanded = expandedDisciplines.includes(discipline);
                              return (
                                <div className='jobsFilterGroup' key={discipline}>
                                  <div className='jobsFilterGroupRow'>
                                    <label className='jobsFilterOption jobsFilterGroupLabel'>
                                      <input
                                        type='checkbox'
                                        checked={isNestedGroupSelected('disciplinesSpecialties', discipline, specialties)}
                                        onChange={() => toggleNestedFilterGroup('disciplinesSpecialties', discipline, specialties)}
                                      />
                                      <span className='jobsFilterCheckboxMark' />
                                      <span>{discipline}</span>
                                    </label>
                                    <button type='button' className='jobsFilterGroupToggle' onClick={() => toggleExpandedGroup(discipline, setExpandedDisciplines)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${discipline}`} aria-expanded={isExpanded}>
                                      <FilterChevron isOpen={isExpanded} />
                                    </button>
                                  </div>
                                  {isExpanded && (
                                    <div className='jobsFilterChildren'>
                                      {specialties.map(specialty => {
                                        const value = createNestedFilterValue(discipline, specialty);
                                        return (
                                          <label className='jobsFilterOption' key={value}>
                                            <input type='checkbox' checked={selectedFilters.disciplinesSpecialties.includes(value)} onChange={() => toggleFilterValue('disciplinesSpecialties', value)} />
                                            <span className='jobsFilterCheckboxMark' />
                                            <span>{specialty}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </section>

                    {[
                      ['shiftPatterns', 'Shift Pattern', filterValues.shiftPatterns],
                      ['shiftTypes', 'Shift Type', filterValues.shiftTypes],
                    ].map(([filterName, title, values]) => (
                      <section className='jobsFilterSection' key={filterName}>
                        <div className='jobsFilterHeader'>
                          <button type='button' className='jobsFilterToggle' onClick={() => toggleSection(filterName)} aria-expanded={expandedSections[filterName]}>
                            <span>{title}</span>
                          </button>
                          {!!selectedFilters[filterName].length && <button type='button' className='jobsFilterReset' onClick={() => resetFilter(filterName)}>Reset</button>}
                          <button type='button' className='jobsFilterSectionChevron' onClick={() => toggleSection(filterName)} aria-label={`${expandedSections[filterName] ? 'Collapse' : 'Expand'} ${title}`} aria-expanded={expandedSections[filterName]}>
                            <FilterChevron isOpen={expandedSections[filterName]} />
                          </button>
                        </div>
                        {expandedSections[filterName] && (
                          <div className='jobsFilterBody'>
                            <div className='jobsFilterOptions'>
                              {values.map(value => (
                                <label className='jobsFilterOption' key={value}>
                                  <input type='checkbox' checked={selectedFilters[filterName].includes(value)} onChange={() => toggleFilterValue(filterName, value)} />
                                  <span className='jobsFilterCheckboxMark' />
                                  <span>{value}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                )}
                <button type='button' className='libraryListItemConterolOpen jobsFiltersApply' onClick={handleSearch}>
                  Apply Filters
                </button>
              </div>
            </div>

            <div className='libraryRight' ref={jobsListTopRef}>
              <div className='libraryListHeader'>
                <span className='libraryListHeaderCount'>{filteredRows.length} OF {totalJobs} JOBS FOUND</span>
                <div className='libraryListHeaderSort'></div>
              </div>
              {!!filteredRows?.length ? (
                <div className='libraryList'>
                  {filteredRows?.map(job => (
                    <div className='libraryListItem' key={job.id}>
                      <span className='libraryListItemTitle'>{job.title}</span>
                      <span className='libraryListItemDetails'>
                        <span className='libraryListItemLocation'>{job.location} •</span>
                        <span className='libraryListItemDate'>{format(new Date(job.created_time), 'MMM dd yyyy')} </span>
                      </span>
                      <span className='libraryListItemSummary'>{job.content}</span>

                      <div className='libraryListItemConterols'>
                        <span
                          className='libraryListItemConterolOpen'
                          onClick={() => setSelectedJob(job)}
                        >
                          Apply
                        </span>
                        <span className='libraryListItemConterolForm' onClick={() => navigate(`/jobs/${job.vms_job_id || job.id}`)}>
                          View Details
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='libraryEmpty'>
                  <span className='libraryEmptyText'>
                    We couldn't find any jobs matching your search criteria.
                    <br /> Try adjusting your search terms to see more results.
                  </span>
                  <span
                    className='libraryEmptyButton libraryListItemConterolForm'
                    onClick={() => {
                      resetAllFilters();
                    }}
                  >
                    Show all jobs
                  </span>
                </div>
              )}

              {(currentPage > 1 || nextPageKey) && (
                <nav className='jobsPagination' aria-label='Jobs pagination'>
                  <button
                    type='button'
                    className='jobsPaginationArrow jobsPaginationArrowPrevious'
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || isLoading}
                    aria-label='Previous page'
                  >
                    <FilterChevron isOpen={false} />
                  </button>
                  <span className='jobsPaginationPage'>Page {currentPage}</span>
                  <button
                    type='button'
                    className='jobsPaginationArrow jobsPaginationArrowNext'
                    onClick={handleNextPage}
                    disabled={!nextPageKey || isLoading}
                    aria-label='Next page'
                  >
                    <FilterChevron isOpen={false} />
                  </button>
                </nav>
              )}
            </div>
          </div>
        ) : (
          <div className='loader'>
            <Loader />
          </div>
        )}
      </div>
      {selectedJob && (
        <div className='jobApplicationModal' role='presentation' onMouseDown={() => setSelectedJob(null)}>
          <div
            className='jobApplicationModalContent'
            role='dialog'
            aria-modal='true'
            aria-label={`Apply for ${selectedJob.title}`}
            onMouseDown={event => event.stopPropagation()}
          >
            <button
              type='button'
              className='jobApplicationModalClose'
              aria-label='Close application form'
              onClick={() => setSelectedJob(null)}
            >
              <svg width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M23 13L13 23M13 13L23 23'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
            <JobApplicationForm job={selectedJob} />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Library;
